/**
 * Die Lieferanten-Uebersicht: eine dichte Tabelle aller Lieferanten mit bis zu vier direkt
 * aenderbaren Zahlungszielen je Zeile (Auftrag 2026-10-01).
 *
 * Datenquelle ist `GET /api/suppliers` (`api/lieferanten.ts#lieferantenUebersichtLaden`), geschrieben
 * wird ueber das bestehende `PUT /api/suppliers/:k/payment-terms` (`freigabenErsetzen`), das die
 * GANZE Liste ersetzt. Daraus folgt die Regel der Tabelle: jede Aenderung berechnet die vollstaendige
 * neue Liste (`neueFreigaben`) - Spalte 1 ist immer der Standard, Luecken gibt es nicht (leert man
 * eine Spalte, ruecken die folgenden nach).
 *
 * Hat ein Lieferant schon MEHR als vier Freigaben, zeigt die Tabelle die ersten vier plus "+n" und
 * sperrt die Zeile: ein Ersetzen mit vier Eintraegen wuerde die uebrigen still loeschen.
 */
import { Fragment, useEffect, useMemo, useState, type JSX } from "react";
import {
  lieferantenUebersichtLaden,
  type LieferantenUebersichtZeile,
  type UebersichtZahlungsziel,
} from "../../api/lieferanten.ts";
import { freigabenErsetzen, type FreigabeZeile } from "../../api/lieferantenZahlungsziele.ts";
import { ladeVorlagen, type PaymentTemplateRow } from "../../api/zahlungsvorlagen.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import { NUR_LESEN_TITEL } from "../Drawer/drawerRechte.ts";
import { aktivMarke } from "./AktivMarke.tsx";

/** Spalten der Tabelle = Hoechstzahl der Zahlungsziele, die sie bearbeitet. */
export const MAX_ZAHLUNGSZIELE = 4;
export const ZU_VIELE_TITEL = "mehr als 4 Zahlungsziele – in der Detailansicht bearbeiten";

export interface FreigabeEintrag {
  templateId: string;
  isDefault: boolean;
}

/**
 * Die vollstaendige neue Liste nach einer Aenderung in Spalte `spalte` (0-basiert). `aktuell` sind
 * die Vorlagen-IDs der bisherigen Zeile in Spaltenreihenfolge, `neueId` ist "" fuer "–".
 * Leere Spalten fallen heraus (Nachruecken), Position 1 wird Standard. `null`, wenn die Vorlage in
 * einer ANDEREN Spalte schon steht - eine Dublette lehnt auch der Server ab.
 */
export function neueFreigaben(
  aktuell: readonly string[],
  spalte: number,
  neueId: string,
): FreigabeEintrag[] | null {
  const spalten = Array.from({ length: MAX_ZAHLUNGSZIELE }, (_, i) => aktuell[i] ?? "");
  if (neueId !== "" && spalten.some((id, i) => i !== spalte && id === neueId)) return null;
  spalten[spalte] = neueId;
  return spalten
    .filter((id) => id !== "")
    .map((templateId, index) => ({ templateId, isDefault: index === 0 }));
}

export interface LieferantenTabelleProps {
  darfSchreiben: boolean;
  /** Suchtext; leer = alle. Gefiltert wird auf den Firmennamen, ohne Gross-/Kleinschreibung. */
  filter: string;
  onOeffnen: (kLieferant: number) => void;
  /** Der Zustand nach erfolgreichem Speichern - haelt Drawer und Bestellung aktuell. */
  onFreigabenGespeichert?: (kLieferant: number, freigaben: FreigabeZeile[]) => void;
  /** Trefferzahl nach Filter und Gesamtzahl, fuer die Anzeige neben dem Suchfeld. */
  onZaehlung?: (treffer: number, gesamt: number) => void;
}

export function LieferantenTabelle({
  darfSchreiben,
  filter,
  onOeffnen,
  onFreigabenGespeichert,
  onZaehlung,
}: LieferantenTabelleProps): JSX.Element {
  const [zeilen, setZeilen] = useState<LieferantenUebersichtZeile[] | null>(null);
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  const [vorlagen, setVorlagen] = useState<PaymentTemplateRow[]>([]);
  const [vorlagenFehler, setVorlagenFehler] = useState<string | null>(null);
  const [speichernd, setSpeichernd] = useState<ReadonlySet<number>>(new Set());
  const [zeilenFehler, setZeilenFehler] = useState<ReadonlyMap<number, string>>(new Map());

  useEffect(() => {
    const abbruch = new AbortController();
    lieferantenUebersichtLaden({ signal: abbruch.signal }).then(
      (geladen) => {
        if (!abbruch.signal.aborted) setZeilen(geladen);
      },
      (grund: unknown) => {
        // Die Flaeche steht schon (R2): neu ist der Text. Ergibt `fehlertext` `null` (Abbruch), bleibt der Satz allein.
        const kontext = "Die Lieferanten konnten nicht geladen werden.";
        if (!abbruch.signal.aborted) setLadefehler(fehlertext(grund, kontext) ?? kontext);
      },
    );
    return () => abbruch.abort();
  }, []);

  useEffect(() => {
    let verworfen = false;
    ladeVorlagen().then(
      (geladen) => {
        if (!verworfen && Array.isArray(geladen)) setVorlagen(geladen);
      },
      (grund: unknown) => {
        const kontext = "Die Zahlungsvorlagen konnten nicht geladen werden.";
        if (!verworfen) setVorlagenFehler(fehlertext(grund, kontext) ?? kontext);
      },
    );
    return () => {
      verworfen = true;
    };
  }, []);

  const sichtbar = useMemo(() => {
    const gesucht = filter.trim().toLowerCase();
    if (zeilen === null) return [];
    return gesucht === "" ? zeilen : zeilen.filter((z) => z.firma.toLowerCase().includes(gesucht));
  }, [zeilen, filter]);

  useEffect(() => {
    if (zeilen !== null) onZaehlung?.(sichtbar.length, zeilen.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sichtbar.length, zeilen?.length]);

  function setzeFehler(k: number, text: string | null): void {
    setZeilenFehler((alt) => {
      const neu = new Map(alt);
      if (text === null) neu.delete(k);
      else neu.set(k, text);
      return neu;
    });
  }

  function labelVon(templateId: string): string {
    return vorlagen.find((v) => v.id === templateId)?.label ?? templateId;
  }

  async function aendere(zeile: LieferantenUebersichtZeile, spalte: number, neueId: string): Promise<void> {
    const k = zeile.kLieferant;
    if (speichernd.has(k)) return;
    // Auch ein change-Ereignis auf eine gesperrte Zeile darf nichts senden: `disabled` haelt nur die
    // Maus fern, und ein Ersetzen mit vier Eintraegen wuerde die uebrigen still loeschen.
    if (zeile.zahlungsziele.length > MAX_ZAHLUNGSZIELE) return;
    const eintraege = neueFreigaben(
      zeile.zahlungsziele.map((z) => z.templateId),
      spalte,
      neueId,
    );
    if (eintraege === null) return;

    const vorher = zeile.zahlungsziele;
    const optimistisch: UebersichtZahlungsziel[] = eintraege.map((e, index) => ({
      templateId: e.templateId,
      label: vorher.find((z) => z.templateId === e.templateId)?.label ?? labelVon(e.templateId),
      isDefault: e.isDefault,
      sortIndex: index,
    }));
    const ersetze = (ziele: UebersichtZahlungsziel[]): void =>
      setZeilen((alt) => (alt === null ? alt : alt.map((z) => (z.kLieferant === k ? { ...z, zahlungsziele: ziele } : z))));

    setzeFehler(k, null);
    setSpeichernd((alt) => new Set(alt).add(k));
    ersetze(optimistisch);
    try {
      const gespeichert = await freigabenErsetzen(k, eintraege);
      ersetze(
        gespeichert.map((g) => ({
          templateId: g.templateId,
          label: g.label,
          isDefault: g.isDefault,
          sortIndex: g.sortIndex,
        })),
      );
      onFreigabenGespeichert?.(k, gespeichert);
    } catch (grund) {
      ersetze(vorher);
      // Inline bleibt inline (Spec R2): die Zeile an der Lieferantenzeile gab es schon, neu ist der Text. `null`
      // (Abbruch) raeumt die Zeile wieder.
      setzeFehler(k, fehlertext(grund, "Die Zahlungsziele konnten nicht gespeichert werden."));
    } finally {
      setSpeichernd((alt) => {
        const neu = new Set(alt);
        neu.delete(k);
        return neu;
      });
    }
  }

  if (ladefehler !== null) {
    return (
      <p className="suppliers-meldung" role="alert">
        {ladefehler}
      </p>
    );
  }
  if (zeilen === null) return <p className="suppliers-leer">Lieferanten werden geladen …</p>;

  return (
    <div className="lieferanten-tabelle-huelle">
      {vorlagenFehler !== null ? (
        <p className="suppliers-meldung" role="status">
          {vorlagenFehler}
        </p>
      ) : null}
      <table className="lieferanten-tabelle">
        <thead>
          <tr>
            <th scope="col">Lieferant</th>
            <th scope="col">Zahlungsziel 1 (Standard)</th>
            <th scope="col">Zahlungsziel 2</th>
            <th scope="col">Zahlungsziel 3</th>
            <th scope="col">Zahlungsziel 4</th>
          </tr>
        </thead>
        <tbody>
          {sichtbar.map((zeile) => {
            const k = zeile.kLieferant;
            const zuViele = zeile.zahlungsziele.length > MAX_ZAHLUNGSZIELE;
            const sperre = zuViele || speichernd.has(k) || !darfSchreiben;
            const sperrTitel = !darfSchreiben ? NUR_LESEN_TITEL : zuViele ? ZU_VIELE_TITEL : undefined;
            const ids = zeile.zahlungsziele.map((z) => z.templateId);
            const fehler = zeilenFehler.get(k);
            const ohneStandard = zeile.zahlungsziele.length > 0 && !zeile.zahlungsziele.some((z) => z.isDefault);
            return (
              <Fragment key={k}>
                <tr className={speichernd.has(k) ? "lieferanten-zeile speichert" : "lieferanten-zeile"}>
                  <td className="lieferanten-name">
                    <div className="lieferanten-name-zeile">
                      <button
                        type="button"
                        className="lieferanten-link"
                        title={zeile.firma}
                        onClick={() => onOeffnen(k)}
                      >
                        {zeile.firma}
                      </button>
                      {aktivMarke(zeile.aktiv)}
                    </div>
                  </td>
                  {Array.from({ length: MAX_ZAHLUNGSZIELE }, (_, spalte) => {
                    const ziel = zeile.zahlungsziele[spalte];
                    const wert = ziel?.templateId ?? "";
                    const text = ziel === undefined ? "–" : ziel.label;
                    return (
                      <td key={spalte} title={sperrTitel}>
                        <div className="lieferanten-zelle">
                        {darfSchreiben ? (
                          <select
                            value={wert}
                            disabled={sperre}
                            title={sperrTitel ?? ziel?.label}
                            aria-label={`Zahlungsziel ${spalte + 1} – ${zeile.firma}`}
                            onChange={(e) => void aendere(zeile, spalte, e.target.value)}
                          >
                            <option value="">–</option>
                            {vorlagen.map((v) => (
                              <option key={v.id} value={v.id} disabled={ids.includes(v.id) && v.id !== wert}>
                                {v.label}
                              </option>
                            ))}
                            {wert !== "" && !vorlagen.some((v) => v.id === wert) ? (
                              <option value={wert}>{text}</option>
                            ) : null}
                          </select>
                        ) : (
                          <span className="lieferanten-text" title={ziel?.label}>
                            {text}
                          </span>
                        )}
                        {spalte === 0 && ohneStandard ? (
                          <span
                            className="lieferanten-kein-standard"
                            title="Keines der Zahlungsziele ist als Standard markiert – Speichern macht Spalte 1 zum Standard"
                          >
                            kein Standard
                          </span>
                        ) : null}
                        {spalte === MAX_ZAHLUNGSZIELE - 1 && zuViele ? (
                          <span className="lieferanten-mehr" title={ZU_VIELE_TITEL}>
                            +{zeile.zahlungsziele.length - MAX_ZAHLUNGSZIELE}
                          </span>
                        ) : null}
                        </div>
                      </td>
                    );
                  })}
                </tr>
                {fehler !== undefined ? (
                  <tr className="lieferanten-fehlerzeile">
                    <td colSpan={1 + MAX_ZAHLUNGSZIELE}>
                      <p className="suppliers-meldung" role="alert">
                        {fehler}
                      </p>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {sichtbar.length === 0 ? (
        <p className="suppliers-leer">
          {zeilen.length === 0 ? "Keine Lieferanten vorhanden." : "Kein Lieferant passt zum Suchtext."}
        </p>
      ) : null}
    </div>
  );
}

